import React from "react";
import { AddDocument } from "@components/documents/FinancialDocumentList/AddDocument";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { TrashIcon } from "@icons";
import { FinancialDocument } from "@common/types/financialDocument";
import { useOverlays } from "contexts/OverlayContext";
import styles from "./AccrualDocumentsCompact.module.scss";
import { useAccrualDocumentLinks } from "./useAccrualDocumentLinks";

interface DocumentRowProps {
    fileName: string;
    onClick: () => void;
    onRemove?: () => void;
    testId?: string;
}

const DocumentRow: React.FC<DocumentRowProps> = ({
    fileName,
    onClick,
    onRemove,
    testId,
}) => (
    <div className={styles.documentRow} data-testid={testId}>
        <Button
            variant="secondary"
            size="sm"
            className={styles.documentName}
            onClick={onClick}
        >
            <span>{fileName}</span>
        </Button>
        {onRemove && (
            <Tooltip content="Remove attachment">
                <Button
                    variant="secondary"
                    iconOnly
                    size="sm"
                    className={styles.removeButton}
                    data-testid="detach-document-button"
                    aria-label={`Unlink ${fileName}`}
                    onClick={onRemove}
                >
                    <TrashIcon />
                </Button>
            </Tooltip>
        )}
    </div>
);

const FinancialDocumentRow: React.FC<{
    financialDocument: FinancialDocument;
    onRemove?: () => void;
}> = ({ financialDocument, onRemove }) => {
    const { openOverlay } = useOverlays();

    return (
        <DocumentRow
            fileName={financialDocument.fileName}
            onClick={() =>
                openOverlay("financialDocumentId", financialDocument.id)
            }
            onRemove={onRemove}
            testId="transaction-document"
        />
    );
};

export interface SyntheticAttachment {
    fileName: string;
    onOpen: () => void;
}

interface AccrualDocumentsCompactProps {
    financialDocumentIds?: number[];
    // Optional so a read-only consumer can omit link/remove callbacks entirely
    // while still displaying existing documents and any generated PDF.
    onLinkDocuments?: (
        documentIds: number[],
        options: { onError: () => void },
    ) => void;
    onRemoveDocument?: (
        documentId: number,
        options: { onError: () => void },
    ) => void;
    // When true, hides the add-document control and per-document remove action
    // but keeps existing documents and the PDF viewable.
    isReadOnly?: boolean;
    pdfAttachment?: SyntheticAttachment;
}

export const AccrualDocumentsCompact: React.FC<
    AccrualDocumentsCompactProps
> = ({
    financialDocumentIds,
    onLinkDocuments,
    onRemoveDocument,
    isReadOnly = false,
    pdfAttachment,
}) => {
    const {
        documentIds,
        attachedDocuments,
        attachDocument,
        attachDocuments,
        handleDocumentsUpload,
        detachDocument,
    } = useAccrualDocumentLinks({
        financialDocumentIds,
        onLinkDocuments,
        onUnlinkDocument: onRemoveDocument,
    });

    const hasAttachments = !!pdfAttachment || attachedDocuments.length > 0;

    return (
        <FinancialDocumentUploadProvider onCompleted={handleDocumentsUpload}>
            <div className={styles.container}>
                <span className={styles.label}>Attachments</span>
                <div className={styles.uploadWrapper}>
                    {!isReadOnly && (
                        <AddDocument
                            attachDocument={attachDocument}
                            attachDocuments={attachDocuments}
                            attachedDocumentIds={documentIds}
                        />
                    )}
                    {hasAttachments && (
                        <div className={styles.list}>
                            {pdfAttachment && (
                                <DocumentRow
                                    fileName={pdfAttachment.fileName}
                                    onClick={pdfAttachment.onOpen}
                                />
                            )}
                            {attachedDocuments.map((document) => (
                                <FinancialDocumentRow
                                    key={document.id}
                                    financialDocument={document}
                                    onRemove={
                                        !isReadOnly && onRemoveDocument
                                            ? () => detachDocument(document.id)
                                            : undefined
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </FinancialDocumentUploadProvider>
    );
};
